import React, { useState, useEffect, useRef } from "react";
import parse from "html-react-parser";
import { useSlideContext } from "../../../../../contexts/slideContext";

import {
  Box,
  Paper,
  Button,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Popover,
  SvgIcon,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ChevronRight } from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { DragIndicator } from "@mui/icons-material";
import { Row, Col } from "react-bootstrap";
import interact from "interactjs";
import AddIcon from "@mui/icons-material/Add";

export default function BlockTree() {
  const [activeSlideBlocks, setActiveSlideBlocks] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [expandedGroupNodes, setExpandedGroupNodes] = useState(new Set());
  const [addBlockAnchorEl, setAddBlockAnchorEl] = useState(null);
  const [hiddenBlocks, setHiddenBlocks] = useState(new Set());
  const [hiddenGroups, setHiddenGroups] = useState(new Set());
  const {
    currentEditorInstanceRef,
    currentSlideRef,
    registerBlockTreeUpdateFunction,
    triggerBlockOrder,
    showBlockTree,
    setShowBlockTree,
    activeSlideLayout,
    setHoveredNode,
    hoveredBlock,
    triggerSelectClick,
    activeSelectedBlocks,
    activeSelectedGroups,
    activeAvailableTools,
    triggerAddBlock,
    triggerHideBlock,
    triggerHideGroup,
  } = useSlideContext();

  const listRef = useRef(null);
  const draggedElRef = useRef(null);
  const placeholderRef = useRef(null);
  const mainContainerRef = useRef(null);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShowBlockTree(false);
      setIsClosing(false);
      // Reset position after close
      if (mainContainerRef.current) {
        const el = mainContainerRef.current;
        el.style.left = "";
        el.style.right = "";
        el.style.top = "";
        delete el.dataset.dragX;
        delete el.dataset.dragY;
      }
    }, 300);
  };

  const clearAllHovered = () => {
    mainContainerRef.current
      .querySelectorAll(`.hovered`)
      .forEach((node) => node.classList.remove("hovered"));
  };

  // Load blocks
  useEffect(() => {
    if (
      currentEditorInstanceRef.current &&
      currentSlideRef.current &&
      currentEditorInstanceRef.current?.blocks
    ) {
      registerBlockTreeUpdateFunction(runUpdateActiveSlideBlocks);
      runUpdateActiveSlideBlocks();
    } else if (
      currentEditorInstanceRef.current === null &&
      currentSlideRef.current === null
    ) {
      setActiveSlideBlocks([]);
    }
    // Note: refs and functions are stable references from context; .current values
    // don't trigger re-renders, so this effect relies on external re-render triggers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentEditorInstanceRef.current,
    currentSlideRef.current,
    activeSelectedGroups,
    activeSelectedBlocks,
  ]);

  useEffect(() => {
    if (hoveredBlock) {
      clearAllHovered();
      const hoveredBlockGroupId = hoveredBlock.groupId;
      if (hoveredBlock.id) {
        const hoveredBlockId = hoveredBlock.id;
        mainContainerRef.current
          .querySelector(`.block-tree-node[data-id="${hoveredBlockId}"]`)
          ?.classList.add("hovered");
      }

      if (hoveredBlockGroupId) {
        const groupedNodes = mainContainerRef.current.querySelectorAll(
          `.group-node[data-group="${hoveredBlockGroupId}"]`,
        );
        groupedNodes.forEach((node) => node.classList.add("hovered"));
      }
    } else {
      clearAllHovered();
    }
  }, [hoveredBlock]);

  useEffect(() => {
    runSelectedBlocksUpdate();
    // eslint-disable-next-line
  }, [activeSelectedGroups, activeSelectedBlocks]);

  useEffect(() => {
    const sidebar = mainContainerRef.current.closest(".ps-sidebar");
    if(sidebar){
      if(showBlockTree && sidebar.style.width === "0px"){
        sidebar.style.width = "260px";
      }
    }
  },[showBlockTree]);

  const runSelectedBlocksUpdate = () => {
    const clearSelected = () => {
      mainContainerRef.current
        .querySelectorAll(`.selected`)
        .forEach((node) => node.classList.remove("selected"));
    };
    clearSelected();
    if (activeSelectedBlocks.size > 0) {
      activeSelectedBlocks.forEach((blockId) => {
        mainContainerRef.current
          .querySelector(`.block-tree-node[data-id="${blockId}"]`)
          ?.classList.add("selected");
      });
    }
    if (activeSelectedGroups.size > 0) {
      activeSelectedGroups.forEach((groupId) => {
        const groupedNodes = mainContainerRef.current.querySelectorAll(
          `.group-node[data-group="${groupId}"]`,
        );
        groupedNodes.forEach((node) => node.classList.add("selected"));
      });
    }
  };

  const runHiddenUpdate = () => {
    setHiddenBlocks(getAllHiddenBlocks());
    setHiddenGroups(getAllHiddenGroups());
  };

  const getAllHiddenBlocks = () => {
    const blockIds = new Set();
    const hiddenBlocks = currentSlideRef.current.querySelectorAll(
      `.ce-block[data-hide="true"]`,
    );
    hiddenBlocks.forEach((block) => {
      blockIds.add(block.dataset.id);
    });
    return blockIds;
  };

  const getAllHiddenGroups = () => {
    const groupIds = new Set();
    const hiddenGroups = currentSlideRef.current.querySelectorAll(
      `.ce-block[data-group-hide="true"]`,
    );
    hiddenGroups.forEach((block) => {
      if (block.dataset.group) {
        groupIds.add(block.dataset.group);
      }
    });
    return groupIds;
  };

  // Triggers changes done on slide
  const runUpdateActiveSlideBlocks = async () => {
    const editor = currentEditorInstanceRef.current;
    const slide = currentSlideRef.current;
    if (!editor || !slide) return;

    const blocks = editor.blocks;
    const blockLength = blocks?.getBlocksCount() ?? 0;

    const ids = await Promise.all(
      Array.from({ length: blockLength }, async (_, i) => {
        const block = blocks.getBlockByIndex(i);
        if (!block?.id) return null;

        let toolboxEntry = null;
        try {
          toolboxEntry = await block.getActiveToolboxEntry(block.name);
        } catch {}

        const foundBlock = slide.querySelector(
          `.ce-block[data-id="${block.id}"]`,
        );

        return {
          id: block.id,
          name: block.name,
          toolboxLabel: toolboxEntry?.title || toolboxEntry?.name || block.name,
          toolboxIcon: toolboxEntry?.icon || null,
          groupId: foundBlock?.getAttribute("data-group") ?? null,
        };
      }),
    );

    normalizeGroupedBlocks(ids, editor);
    setActiveSlideBlocks(ids.filter(Boolean));
    runSelectedBlocksUpdate();
    runHiddenUpdate();
  };

  const normalizeGroupedBlocks = (_blocks, editor) => {
    const groupIds = [
      ...new Set(_blocks.map((block) => block.groupId).filter(Boolean)),
    ];

    for (const groupId of groupIds) {
      const groupedBlocks = _blocks.filter((b) => b.groupId === groupId);

      let startingIndex;

      const reversed = [...groupedBlocks].reverse();

      for (let index = 0; index < reversed.length; index++) {
        const groupedBlock = reversed[index];
        const blockIndex = _blocks.findIndex(
          (_block) => _block.id === groupedBlock.id,
        );

        if (index === 0) {
          startingIndex = blockIndex;
          continue;
        }

        if (startingIndex - index !== blockIndex) {
          editor.blocks.move(startingIndex - index, blockIndex);

          break;
        }
      }
    }
  };

  const processDragEnd = () => {
    const blocks = [...listRef.current.querySelectorAll(".block-tree-node")];
    const blockIds = blocks.map((b) => b.dataset.id).reverse();
    setIsDragging(false);
    triggerBlockOrder(blockIds);
  };

  // Setup interact.js draggable behavior
  useEffect(() => {
    interact(".draggable").draggable({
      // inertia: true,
      // autoScroll: true,
      allowFrom: ".drag-handle",
      listeners: {
        start(event) {
          const el = event.target;
          draggedElRef.current = el;

          const rect = el.getBoundingClientRect();
          let listRect, left, top;
          if (!el.classList.contains("grouped")) {
            listRect = listRef.current.getBoundingClientRect();

            left = rect.left - listRect.left + listRef.current.scrollLeft;
            top = rect.top - listRect.top + listRef.current.scrollTop;
          } else {
            const groupNodeList = el.closest(".group-node-list");
            listRect = groupNodeList.getBoundingClientRect();

            left = rect.left - listRect.left + groupNodeList.scrollLeft;
            top = rect.top - listRect.top + groupNodeList.scrollTop;
          }

          // Adjust rect to be relative to the list container

          // Create placeholder
          const placeholder = document.createElement("div");
          placeholder.className = "placeholder";
          placeholder.style.height = `${rect.height}px`;
          placeholderRef.current = placeholder;

          el.parentNode.insertBefore(placeholder, el.nextSibling);

          el.classList.add("dragging");
          el.style.width = `${rect.width}px`;
          el.style.left = `${left}px`;
          el.style.top = `${top}px`;
          el.style.position = "absolute";

          el.dataset.x = left;
          el.dataset.y = top;
        },

        move(event) {
          const el = draggedElRef.current;
          if (!el) return;

          let x = parseFloat(el.dataset.x) + event.dx;
          let y = parseFloat(el.dataset.y) + event.dy;

          el.style.left = `${x}px`;
          el.style.top = `${y}px`;
          el.dataset.x = x;
          el.dataset.y = y;
          const draggedRect = el.getBoundingClientRect();
          let list, children;
          if (!el.classList.contains("grouped")) {
            list = listRef.current;
            children = [
              ...list.querySelectorAll(".draggable:not(.dragging, .grouped)"),
            ];
          } else {
            list = el.closest(".group-node-list");

            children = [...list.querySelectorAll(".draggable:not(.dragging)")];
          }

          for (const child of children) {
            const rect = child.getBoundingClientRect();
            const midpoint = rect.top + rect.height / 2;

            if (draggedRect.top < midpoint) {
              list.insertBefore(placeholderRef.current, child);
              return;
            }
          }

          list.appendChild(placeholderRef.current);
        },

        end() {
          const el = draggedElRef.current;
          const placeholder = placeholderRef.current;
          const list = el.classList.contains("grouped")
            ? el.closest(".group-node-list")
            : listRef.current;

          if (!el || !placeholder) return;

          list.insertBefore(el, placeholder);

          el.classList.remove("dragging");
          el.style.left = "";
          el.style.top = "";
          el.style.width = "";
          el.style.position = "";
          delete el.dataset.x;
          delete el.dataset.y;

          placeholder.remove();
          draggedElRef.current = null;
          placeholderRef.current = null;
          if (triggerBlockOrder) {
            triggerBlockOrder("test");
          }
          processDragEnd();
        },
      },
    });

    return () => interact(".draggable").unset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSlideBlocks]);

  const renderBlocks = () => {
    if (activeSlideBlocks.length > 0) {
      const tempActiveSlideBlocks = [...activeSlideBlocks].reverse();
      const orderedBlocks = [];
      const groupMap = {};

      for (const block of tempActiveSlideBlocks) {
        const { groupId } = block;

        if (!groupId) {
          orderedBlocks.push({ renderType: "block", ...block });
          continue;
        }

        if (!groupMap[groupId]) {
          const group = {
            groupId,
            renderType: "group",
            blocks: [],
          };

          groupMap[groupId] = group;
          orderedBlocks.push(group);
        }

        groupMap[groupId].blocks.push(block);
      }

      return (
        <>
          {orderedBlocks.map((element) =>
            element?.renderType === "group"
              ? GroupNode(element)
              : BlockNode(element),
          )}
        </>
      );
    }
  };

  const GroupNode = (blockGroup) => {
    return (
      <Accordion
        key={blockGroup.groupId}
        data-group={blockGroup.groupId}
        className={`draggable group-node ${activeSelectedGroups.has(blockGroup.groupId) ? "selected" : ""} ${expandedGroupNodes.has(blockGroup.groupId) ? "expanded" : ""}`}
        disableGutters
        expanded={expandedGroupNodes.has(blockGroup.groupId)}
        elevation={0}
        onMouseEnter={() => {
          clearAllHovered();
          if (isDragging) return;
          setHoveredNode({ type: "group", id: blockGroup.groupId });
        }}
        onMouseLeave={() => {
          if (isDragging) return;
          setHoveredNode(null);
          setIsDragging(false);
        }}
        onPointerDown={() => setIsDragging(true)}
        onPointerUp={() => setIsDragging(false)}
      >
        <AccordionSummary
          onChange={() => {}}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Row className="d-flex align-items-center gx-0 w-100">
            <Col className="px-0" xs="auto">
              <Button
                className="group-node-chevron"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedGroupNodes((prev) => {
                    const newSet = new Set(prev);
                    if (newSet.has(blockGroup.groupId)) {
                      newSet.delete(blockGroup.groupId);
                    } else {
                      newSet.add(blockGroup.groupId);
                    }
                    return newSet;
                  });
                }}
              >
                <ChevronRight
                  sx={
                    expandedGroupNodes.has(blockGroup.groupId)
                      ? { transform: "rotate(90deg)" }
                      : {}
                  }
                />
              </Button>
            </Col>
            <Col
              xs="auto"
              className="flex-grow-1 ps-0"
              onClick={() =>
                triggerSelectClick("group-frame", blockGroup.groupId)
              }
            >
              <Row className="gx-0">
                <Col className="flex-grow-1 ps-0">
                  <Typography
                    className="group-node-label"
                    variant="caption"
                    owner={"admin"}
                    color={"adminPrimary.main"}
                    weight="medium"
                  >
                    Group
                  </Typography>
                </Col>
              </Row>
            </Col>
            <Col xs="auto" className="ps-1 pe-0">
              <IconButton
                className="drag-handle"
                sx={{ color: "adminPrimary.main" }}
              >
                <DragIndicator sx={{ fontSize: 16, cursor: "move" }} />
              </IconButton>
            </Col>
            <Col xs="auto" className="px-0">
              <IconButton
                className="visibility-toggle"
                onClick={() => triggerHideGroup(blockGroup.groupId)}
                sx={{
                  color: hiddenGroups.has(blockGroup.groupId)
                    ? "solid.main"
                    : "adminPrimary.main",
                }}
              >
                {hiddenGroups.has(blockGroup.groupId) ? (
                  <VisibilityOffIcon sx={{ fontSize: 16 }} />
                ) : (
                  <VisibilityIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Col>
          </Row>
        </AccordionSummary>
        <AccordionDetails className="group-node-list">
          {blockGroup.blocks.map((block) =>
            BlockNode(block, true, blockGroup.groupId),
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  const BlockNode = (block, grouped, groupId) => {
    return (
      <Box
        className={`block-tree-node draggable ${grouped ? "grouped" : ""}`}
        key={block.id}
        data-id={block.id}
        // data-index={_index}
        onMouseEnter={() => {
          clearAllHovered();
          if (isDragging) return;
          setHoveredNode({ type: "block", id: block.id });
        }}
        onMouseLeave={() => {
          if (isDragging) return;
          setHoveredNode(null);
          setIsDragging(false);
        }}
        onPointerDown={() => setIsDragging(true)}
        onPointerUp={() => setIsDragging(false)}
      >
        <Row className="gx-0 w-100">
          <Col
            onClick={() => {
              if (groupId) {
                if (activeSelectedGroups.has(groupId)) {
                  triggerSelectClick("block", block.id);
                } else {
                  triggerSelectClick("group-frame", groupId);
                }
              } else {
                triggerSelectClick("block", block.id);
              }
            }}
            xs="auto"
            className="content-col flex-grow-1 ps-0"
          >
            <Row className="gx-0">
              <Col xs="auto" className="ps-0">
                <div
                  className="block-tree-node-icon"
                  dangerouslySetInnerHTML={{
                    __html: block.toolboxIcon || "",
                  }}
                />
              </Col>
              <Col className="flex-grow-1 ps-0">
                <Typography
                  variant="caption"
                  owner={"admin"}
                  color={"clientText.dark"}
                  weight="medium"
                >
                  {blockNameSwitch(block.toolboxLabel ?? block.name)}{" "}
                </Typography>
              </Col>
            </Row>
          </Col>
          <Col xs="auto" className="ps-1 pe-0">
            <Button className="drag-handle">
              <DragIndicator sx={{ fontSize: 16, cursor: "move" }} />
            </Button>
          </Col>
          <Col xs="auto" className="px-0">
            <Button
              className="visibility-toggle"
              onClick={() => triggerHideBlock(block.id)}
              sx={{
                color: hiddenBlocks.has(block.id)
                  ? "solid.main"
                  : "adminPrimary.main",
              }}
            >
              {hiddenBlocks.has(block.id) ? (
                <VisibilityOffIcon sx={{ fontSize: 16 }} />
              ) : (
                <VisibilityIcon sx={{ fontSize: 16 }} />
              )}
            </Button>
          </Col>
        </Row>
      </Box>
    );
  };

  const blockNameSwitch = (blockName) => {
    if (blockName) {
      switch (blockName.toLowerCase()) {
        case "slide title":
          return "Title";
        case "slide subtitle":
          return "Subtitle";
        case "primary image":
          return "Image";
        case "button group":
          return "Button";
        default:
          return blockName;
      }
    }
  };

  const popoverItem = (block) => {
    return (
      <Row className="gx-0" key={`block-${block.key}`}>
        <Button
          className="justify-content-start block-tree-add-block-item"
          startIcon={
            <SvgIcon sx={{ fontSize: 16 }}>{parse(block.icon)}</SvgIcon>
          }
          onClick={() => {
            triggerAddBlock(block.key);
            setAddBlockAnchorEl(null);
          }}
        >
          <Typography owner={"admin"} variant="body2">
            {blockNameSwitch(block.title)}
          </Typography>
        </Button>
      </Row>
    );
  };

  const addBlockMenuOpen = Boolean(addBlockAnchorEl);

  return (
    <Box
      ref={mainContainerRef}
      className={`block-tree-main-container ${showBlockTree && activeSlideLayout === "custom" ? "expanded" : ""} ${isClosing ? "closing" : ""}`}
      sx={{
        // height: 768,
        // maxWidth: 500,
        // minWidth: 200,
        // minHeight: 250,
        maxHeight: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Paper
        className="block-tree-paper-container"
        elevation={3}
        sx={{ height: "100%", display: "flex", flexDirection: "column" }}
      >
        <Box
          className="block-tree-inner-container"
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Stack
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            className="block-tree-header d-flex justify-content-between align-items-center"
            spacing={1}
          >
            <Typography
              className="my-2"
              variant="body2"
              owner="admin"
              color="adminText.dark"
              lh="medium"
            >
              Block Tree
            </Typography>

            <Tooltip title={"Hide Block Tree"} placement="bottom">
              <Button
                variant="text"
                className="block-tree-toggle-btn"
                onClick={handleClose}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </Button>
            </Tooltip>
          </Stack>
          <Box
            className="block-tree-list-container"
            ref={listRef}
            sx={{ flex: 1, overflowY: "auto" }}
          >
            {activeAvailableTools !== null && (
              <>
                <Row className="w-100 gx-0">
                  <Col xs={12}>
                    <Button
                      className="w-100 add-block-btn"
                      startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                      onClick={(e) => {
                        setAddBlockAnchorEl(e.currentTarget);
                      }}
                    >
                      <Typography owner={"adminPrimary.main"} variant="caption">
                        Add Block
                      </Typography>
                    </Button>
                  </Col>
                </Row>
                <Popover
                  className="block-tree-add-block-popover"
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                  slotProps={{
                    paper: {
                      sx: {
                        // width: "200px",
                        maxWidth: "unset",
                      },
                    },
                  }}
                  open={addBlockMenuOpen}
                  anchorEl={addBlockAnchorEl}
                  onClose={() => setAddBlockAnchorEl(null)}
                >
                  <Row className="gx-0 py-2">
                    <Col className="px-3">
                      {activeAvailableTools.map((tool) => popoverItem(tool))}
                    </Col>
                  </Row>
                </Popover>
              </>
            )}
            {activeSlideBlocks?.length > 0 ? (
              renderBlocks()
            ) : (
              <Typography
                variant="body2"
                sx={{ padding: "10px", color: "#777" }}
              >
                No blocks available
              </Typography>
            )}
            {/* {activeSlideBlocks.length > 0 ? (
              [...activeSlideBlocks]
                .reverse()
                .map((block, index) => BlockNode(block, index))
            ) : (
              <Typography
                variant="body2"
                sx={{ padding: "10px", color: "#777" }}
              >
                No blocks available
              </Typography>
            )} */}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
